import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ActivatedRoute, type Params, Router } from '@angular/router';
import { CSSValuePipe } from '@pipes/css-value-pipe';
import { AsSignalPipe } from '@pipes/as-signal-pipe';
import { Button } from '@shared/button/button';
import type { CssColor, FontWeight, TabItem } from '@appTypes';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [Button, CSSValuePipe, AsSignalPipe],
  templateUrl: './tabs.html',
  styleUrl: './tabs.scss',
})
export class Tabs {
  // Dependencias
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Inputs
  $items = input.required<TabItem[]>({ alias: 'items' });
  $activeIndex = input<number | null>(null, { alias: 'activeIndex' });
  $activeIndicator = input<'border' | 'dot'>('dot', { alias: 'activeIndicator' });
  $relativeToCurrentRoute = input<boolean>(true, { alias: 'relativeToCurrentRoute' });
  $reUseQueryParams = input<boolean>(true, { alias: 'ignoreQueryParams' });
  $iconsColor = input<CssColor>('--primary-secondary', { alias: 'iconsColor' });
  $inactiveBackground = input<boolean>(false, { alias: 'inactiveBackground' });
  $borderRadius = input<string>('', { alias: 'borderRadius' });
  $topBottomBorders = input<boolean>(true, { alias: 'topBottomBorders' });
  $leftRightBorders = input<boolean>(true, { alias: 'leftRightBorders' });
  $textWeight = input<FontWeight>('bold', { alias: 'textWeight' });
  $position = input<'horizontal' | 'vertical'>('horizontal', { alias: 'position' });

  // Signals computados
  $firstColumnBorderRadius = computed<string>(() => {
    const matches = Array.from(this.$borderRadius().trim().matchAll(/\S+/g));
    switch (matches.length) {
      case 1:
        return `border-top-left-radius: ${ matches[0] }; border-bottom-left-radius: ${ matches[0] };`;
      case 2:
        return `border-top-left-radius: ${ matches[0] }; border-bottom-left-radius: ${ matches[1] };`;
      case 3:
        return `border-top-left-radius: ${ matches[0] }; border-bottom-left-radius: ${ matches[1] };`;
      case 4:
        return `border-top-left-radius: ${ matches[0] }; border-bottom-left-radius: ${ matches[3] };`;
      default:
        return `border-top-left-radius: unset; border-bottom-left-radius: unset;`;
    }
  });

  $lastColumnBorderRadius = computed<string>(() => {
    const matches = Array.from(this.$borderRadius().trim().matchAll(/\S+/g));
    switch (matches.length) {
      case 1:
        return `border-top-right-radius: ${ matches[0] }; border-bottom-right-radius: ${ matches[0] };`;
      case 2:
        return `border-top-right-radius: ${ matches[1] }; border-bottom-right-radius: ${ matches[0] };`;
      case 3:
        return `border-top-right-radius: ${ matches[1] }; border-bottom-right-radius: ${ matches[2] };`;
      case 4:
        return `border-top-right-radius: ${ matches[1] }; border-bottom-right-radius: ${ matches[2] };`;
      default:
        return `border-top-right-radius: unset; border-bottom-right-radius: unset;`;
    }
  });

  // Signals / atributos
  $currentTab = signal<TabItem | null>(null);

  constructor() {
    // Verificar si habrá una pestaña seleccionada por defecto
    effect(() => {
      const activeIndex = this.$activeIndex();
      const items = this.$items();
      if (activeIndex !== null && activeIndex >= 0 && items.length - 1 >= activeIndex)
        this.$currentTab.set(items[activeIndex]);
    });
  }

  // Maneja el evento de click sobre una pestaña
  tabClicked(tabItem: TabItem) {
    // Establecer la pestaña actual
    this.$currentTab.set(tabItem);

    // Realiza la navegación
    if (tabItem.nav) {
      const relativeTo = this.$relativeToCurrentRoute() ? this.route : null;
      let queryParams: Params = this.route.snapshot.queryParams;
      if (!this.$reUseQueryParams())
        queryParams = {};
      if (tabItem.nav.queryParams)
        queryParams = { ...queryParams, ...tabItem.nav.queryParams };

      this.router.navigate([ tabItem.nav.url ], { relativeTo, queryParams });
    }

    // Realiza el llamado al callback del botón
    tabItem.callback && tabItem.callback();
  }
}
